import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, from, map, of, switchMap, withLatestFrom } from 'rxjs';
import * as OrganisationUserActions from './organisation-user.actions';
import * as OrganisationUserSelectors from './organisation-user.selectors';
import { OrganisationUserService } from '../services/organisation-user.service';
import { OrganisationUserDataRepository } from './organisation-user-data-repository.service';
import { AuthenticationService, AuthSelectors } from '../../authentication';
import { UserService, UsersSelectors } from '../../user';

@Injectable()
export class OrganisationUserEffects {
  private actions$ = inject(Actions);
  private store = inject(Store)
  private authenticationService = inject(AuthenticationService)
  private organisationUsersApiService = inject(OrganisationUserService)
  private organisationUsersDataRepository = inject(OrganisationUserDataRepository)
  private userService = inject(UserService)

  fetchUserOrganisations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationUserActions.fetchUserOrganisations),
      exhaustMap(() =>
        from(this.organisationUsersDataRepository.getAuthenticatedUserOrganisations()).pipe(
          map((orgs) =>
            OrganisationUserActions.fetchUserOrganisationsSuccess({ organisations: orgs ?? [] })
          )
        )
      ),
      catchError((error) => {
        return of(OrganisationUserActions.fetchUserOrganisationsSuccess({ organisations: [] }));
      })
    )
  );

  /**
   * Restore the active organisation from the user's `defaultOrgId` once the org list has loaded.
   * Reacts to the success action (reducers run before effects, so the org list is already fresh),
   * and restores with `persist: false` so it never writes back to the user — which is what
   * previously fed the company <-> data-fetch loop. Re-selecting the already-active org is
   * de-duped by the idempotency guard in `switchUserOrganisation$`.
   */
  restoreActiveOrganisationOnFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationUserActions.fetchUserOrganisationsSuccess),
      withLatestFrom(
        this.store.select(OrganisationUserSelectors.selectUserOrganisations),
        this.store.select(UsersSelectors.selectAuthenticatedUser)
      ),
      map(([, organisations, user]) => {
        const defaultOrgId = user?.defaultOrgId;
        const target = defaultOrgId
          ? (organisations ?? []).find((org) => org._id == defaultOrgId) ?? null
          : null;
        return OrganisationUserActions.switchUserOrganisation({ organisation: target, persist: false });
      })
    )
  );

  switchUserOrganisation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationUserActions.switchUserOrganisation),
      withLatestFrom(this.store.select(AuthSelectors.selectActiveAccountId)),
      switchMap(([props, activeAccountId]) => {
        // Idempotency guard: switching to the already-active org is a no-op, so no
        // `setActiveAccount` / `fetchAuthenticatedUserSuccess` is emitted and the loop cannot start.
        const targetId = props.organisation?._id;
        if (targetId && targetId === activeAccountId) {
          return of(OrganisationUserActions.switchUserOrganisationSuccess());
        }
        if (props.persist) {
          this.userService.setUserDefaultOrganisation(props.organisation ? props.organisation._id : null);
        }
        this.authenticationService.setActiveEntity(props.organisation, UsersSelectors.selectAuthenticatedUser)
        return of(OrganisationUserActions.switchUserOrganisationSuccess())
      })
    )
  )

  addAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationUserActions.addAccount),
      exhaustMap((props) =>
        of(OrganisationUserActions.loadOrganisationUsersForAccount({
          account_id: props.accountBasedEntity.account_id,
          accountType: props.accountBasedEntity.accountType
        }))
      )
    )
  );

  loadSharesForAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationUserActions.loadOrganisationUsersForAccount),
      exhaustMap((props) => {
        return from(this.organisationUsersApiService.readAll()).pipe(
          map(response => {
            const orgUsers = response?.data ? response.data : []
            return OrganisationUserActions.loadOrganisationUsersForAccountSuccess({
              organisationUsers: orgUsers,
              account_id: props.account_id
            })
          })
        )
      }
      )
    )
  );

}
